import { ArrowLeftOutlined, ShareAltOutlined } from '@ant-design/icons';
import { Button, Typography } from 'custom-test-antd';
import React, { useState } from 'react';

import './OnboardingSummary.pcss';

import { OnboardingDto } from 'api/organization/onboardingTypes';

import { BranchRegistrationWorkflowSearchResult } from './BranchRegistrationWorkflowSearchResult';
import { InviteCollaboratorsModal } from './InviteCollaboratorsModal';

const { Title, Paragraph } = Typography;

interface OnboardingSummaryProps {
  userIsOrganizationAdmin: boolean;
  flowId: string | null;
  onboarding: OnboardingDto;
  onExcludeBranchFromRegistrationWorkflow: (credentialId: string, provider: string) => Promise<void>;
  onIncludeBranchToRegistrationWorkflow: (credentialId: string, provider: string) => Promise<void>;
  onShareFlow: (collaboratorEmail?: string) => Promise<void>;
  onBack: () => void;
  onComplete: () => Promise<void>;
}

export const OnboardingSummary: React.FC<OnboardingSummaryProps> = ({
  userIsOrganizationAdmin,
  flowId,
  onboarding,
  onExcludeBranchFromRegistrationWorkflow,
  onIncludeBranchToRegistrationWorkflow,
  onShareFlow,
  onBack,
  onComplete,
}) => {
  const [isInviteModalOpen, setInviteModalOpen] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  return (
    <>
      <div className="onboarding-summary-container">
        <div className="summary-card">
          <div className="summary-header">
            <Title level={2} className="summary-title">
              Onboarding Summary
            </Title>
            <Paragraph className="summary-description">
              Review the status of your added credentials. You can add more via the Share button if needed.
            </Paragraph>
          </div>
          <div className="credential-status-section">
            <Title level={3} className="section-title">
              Credential Status
            </Title>
            <div className="credential-status-list">
              {onboarding.branchRegistrationWorkflows.map((credential) => (
                <div key={credential.id} className="credential-item">
                  <div className="credential-info">
                    <div className="portal-name">{credential.portal}</div>
                    <div className="login">{credential.login}</div>
                  </div>
                  {credential.error == null && credential.areCredentialsValid ? (
                    <BranchRegistrationWorkflowSearchResult
                      branchesTaken={onboarding.branchesBelongingToOtherOrganizations}
                      branchRegistrationWorkflow={credential}
                      branchRegistrationWorkflowCanBeManaged={userIsOrganizationAdmin || credential.isOwn}
                      onExcludeBranchFromRegistrationWorkflow={onExcludeBranchFromRegistrationWorkflow}
                      onIncludeBranchToRegistrationWorkflow={onIncludeBranchToRegistrationWorkflow}
                    />
                  ) : (
                    <div className="invalid-status">
                      <span>{credential.error ?? 'Invalid Credentials'}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="divider" />
          <div className="actions-container">
            <Button icon={<ArrowLeftOutlined />} onClick={onBack} className="back-button">
              Back
            </Button>
            <Button
              icon={<ShareAltOutlined />}
              className="share-button"
              disabled={flowId != null}
              onClick={() => setInviteModalOpen(true)}
            >
              Share with your colleague
            </Button>
            <Button
              type="primary"
              className="continue-button"
              disabled={
                onboarding.branchRegistrationWorkflows.length === 0 ||
                onboarding.branchRegistrationWorkflows.some(
                  (v) => v.areCredentialsValid && v.foundProviders == null && v.error == null,
                )
              }
              onClick={async () => {
                setIsCompleting(true);
                await onComplete();
                setIsCompleting(false);
              }}
              loading={isCompleting}
            >
              Done
            </Button>
          </div>
        </div>
      </div>
      <InviteCollaboratorsModal
        open={isInviteModalOpen}
        onClose={() => {
          setInviteModalOpen(false);
        }}
        onShareFlow={onShareFlow}
      />
    </>
  );
};

export default OnboardingSummary;
